import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SignaturePad } from '@/components/SignaturePad';
import {
  CheckCircle2, XCircle, ArrowRight, ArrowLeft, FileText, ShieldCheck,
  Download, Sparkles, PenLine, Loader2, Building2, ChevronDown, ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayMMDDYYYY = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const scoreOf = (checklist) => {
  const needed = checklist.filter((c) => c.needed);
  if (needed.length === 0) return 100;
  const met = needed.filter((c) => c.met).length;
  return Math.round((100 * met) / needed.length);
};

const makeSampleSignature = (name) => {
  const canvas = document.createElement('canvas');
  canvas.width = 400; canvas.height = 150;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 400, 150);
  ctx.fillStyle = '#1e40af';
  ctx.font = 'italic 46px "Segoe Script", "Brush Script MT", cursive';
  ctx.fillText(name, 24, 92);
  return canvas.toDataURL('image/png');
};

const Stepper = ({ step }) => {
  const steps = ['Review', 'DrawReady', 'Sign', 'Download'];
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              i < step ? 'bg-green-500 text-white' : i === step ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${i === step ? 'text-slate-900' : 'text-slate-400'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-6 sm:w-10 h-0.5 ${i < step ? 'bg-green-500' : 'bg-slate-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

// DrawReady chip mirrors the in-app pill: green "DrawReady" at 100, else the number.
const ScorePill = ({ score }) => {
  if (score >= 100) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <ShieldCheck className="w-3.5 h-3.5" /> DrawReady
      </span>
    );
  }
  const cls = score < 50 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}>
      <ShieldCheck className="w-3.5 h-3.5" /> {score}
    </span>
  );
};

// A single toggle switch (mirrors the in-app "needed" toggle)
const Toggle = ({ on, onChange, testid }) => (
  <button
    type="button"
    onClick={onChange}
    data-testid={testid}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${on ? 'bg-blue-600' : 'bg-slate-300'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

const FirstDraw = () => {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [step, setStep] = useState(0);
  const [signature, setSignature] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);
  const signName = 'Jordan Rivera';

  useEffect(() => {
    fetch(`${API}/api/demo/first-draw`)
      .then((r) => r.json())
      .then((d) => { setProject(d.project); setItems(d.items); setTotal(d.total); setExpanded(d.items?.[0]?.id ?? null); })
      .catch(() => {});
  }, []);

  const updateCheck = (itemId, key, patch) => {
    setItems((prev) => prev.map((it) => it.id !== itemId ? it : {
      ...it,
      checklist: it.checklist.map((c) => c.key === key ? { ...c, ...patch } : c),
    }));
  };

  const completeItem = (itemId) => {
    setItems((prev) => prev.map((it) => it.id !== itemId ? it : {
      ...it,
      checklist: it.checklist.map((c) => ({ ...c, met: c.needed ? true : c.met })),
    }));
  };

  const completeAll = () => {
    setItems((prev) => prev.map((it) => ({
      ...it,
      checklist: it.checklist.map((c) => ({ ...c, met: c.needed ? true : c.met })),
    })));
  };

  const allReady = items.length > 0 && items.every((it) => scoreOf(it.checklist) >= 100);

  const useSample = () => setSignature(makeSampleSignature(signName));

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/demo/first-draw-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_image: signature, sign_name: signName }),
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'DrawReady-Sample-Draw-Request.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDone(true);
      setStep(3);
    } catch (e) {
      // button re-enables
    } finally {
      setGenerating(false);
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="first-draw-logo">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold">D</div>
            <span className="font-bold text-lg">DrawReady</span>
          </button>
          <span className="text-xs px-3 py-1 rounded-full bg-blue-600/30 text-blue-200 border border-blue-500/40">
            Sample walkthrough · no signup
          </span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Build your first bank-ready draw</h1>
          <p className="text-slate-500 mt-2">A sample project so you can get every line item to DrawReady and download the PDF.</p>
        </div>

        <Stepper step={step} />

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
          {/* STEP 0 — Review line items */}
          {step === 0 && (
            <div data-testid="first-draw-step-review">
              <div className="flex items-center gap-2 mb-1 text-slate-500 text-sm">
                <Building2 className="w-4 h-4" /> {project.name} · {project.address}
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-4">Review your draw line items</h2>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 bg-slate-900 text-white text-xs font-semibold px-4 py-2">
                  <div className="col-span-7">Subcontractor / Supplier</div>
                  <div className="col-span-2">Cat #</div>
                  <div className="col-span-3 text-right">Amount</div>
                </div>
                {items.map((it, i) => (
                  <div key={it.id} className={`grid grid-cols-12 px-4 py-3 text-sm ${i % 2 ? 'bg-slate-50' : 'bg-white'}`} data-testid={`draw-line-${i}`}>
                    <div className="col-span-7">
                      <div className="font-medium text-slate-800">{it.name}</div>
                      <div className="text-xs text-slate-400">{it.item_label}</div>
                    </div>
                    <div className="col-span-2 text-slate-500">{it.cat || '—'}</div>
                    <div className="col-span-3 text-right font-mono font-semibold text-slate-900">{fmt(it.amount)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-12 px-4 py-3 border-t-2 border-blue-500 bg-white">
                  <div className="col-span-9 font-bold text-slate-900">TOTAL REQUESTED</div>
                  <div className="col-span-3 text-right font-mono font-bold text-blue-600 text-lg" data-testid="draw-total">{fmt(total)}</div>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <Button onClick={() => setStep(1)} className="bg-blue-600 hover:bg-blue-700" data-testid="first-draw-next-0">
                  Next: get to DrawReady <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 1 — Interactive Draw Readiness (mirrors the in-app popover) */}
          {step === 1 && (
            <div data-testid="first-draw-step-compliance">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Get every item to DrawReady</h2>
              <p className="text-sm text-slate-500 mb-4">
                Each line item scores 0–100. Attach what it needs — or toggle off a step it doesn't — until every item hits 100.
              </p>

              <div className="space-y-3">
                {items.map((it) => {
                  const score = scoreOf(it.checklist);
                  const open = expanded === it.id;
                  return (
                    <div key={it.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`readiness-item-${it.id}`}>
                      <button
                        onClick={() => setExpanded(open ? null : it.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                        data-testid={`readiness-toggle-${it.id}`}
                      >
                        <ScorePill score={score} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">{it.name}</div>
                          <div className="text-xs text-slate-400 truncate">{it.item_label} · {fmt(it.amount)}</div>
                        </div>
                        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>

                      {open && (
                        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Draw readiness</p>
                            <span className="text-sm font-bold text-slate-700" data-testid={`readiness-score-${it.id}`}>{score}/100</span>
                          </div>
                          <p className="text-xs text-slate-400 mb-3">Toggle what this item needs. Un-needed steps stop counting.</p>
                          <div className="space-y-2">
                            {it.checklist.map((c) => (
                              <div key={c.key} className="flex items-center gap-3">
                                {c.met ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                ) : (
                                  <XCircle className={`w-4 h-4 shrink-0 ${c.needed ? 'text-red-400' : 'text-slate-300'}`} />
                                )}
                                <span className={`text-sm flex-1 ${c.needed ? 'text-slate-700' : 'text-slate-400 line-through'}`}>{c.label}</span>
                                {c.needed && !c.met && (
                                  <button
                                    onClick={() => updateCheck(it.id, c.key, { met: true })}
                                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 px-2 py-1 rounded-md hover:bg-blue-50"
                                    data-testid={`attach-${it.id}-${c.key}`}
                                  >
                                    Attach
                                  </button>
                                )}
                                <Toggle
                                  on={c.needed}
                                  onChange={() => updateCheck(it.id, c.key, { needed: !c.needed })}
                                  testid={`needed-${it.id}-${c.key}`}
                                />
                              </div>
                            ))}
                          </div>
                          {score < 100 && (
                            <button
                              onClick={() => completeItem(it.id)}
                              className="mt-3 text-xs font-semibold text-blue-600 hover:text-blue-700"
                              data-testid={`complete-item-${it.id}`}
                            >
                              Complete remaining for this item →
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {allReady ? (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800" data-testid="first-draw-all-ready">
                  <ShieldCheck className="w-5 h-5" /> All line items are DrawReady (100) — this draw is bank-ready.
                </div>
              ) : (
                <button onClick={completeAll} className="mt-4 text-sm font-semibold text-blue-600 hover:text-blue-700" data-testid="first-draw-complete-all">
                  Complete all remaining items →
                </button>
              )}

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(0)} data-testid="first-draw-back-1"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                <Button onClick={() => setStep(2)} disabled={!allReady} className="bg-blue-600 hover:bg-blue-700" data-testid="first-draw-next-1">
                  Next: sign <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2 — Sign */}
          {step === 2 && (
            <div data-testid="first-draw-step-sign">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Sign the draw request</h2>
              <p className="text-sm text-slate-500 mb-4">Draw a signature below, or use a sample one. Date: {todayMMDDYYYY()}</p>
              <div className="max-w-md">
                <SignaturePad width={400} height={150} onSave={setSignature} onClear={() => setSignature(null)} />
                <button onClick={useSample} className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="first-draw-use-sample-sig">
                  <PenLine className="w-4 h-4" /> Use a sample signature
                </button>
              </div>
              {signature && (
                <div className="mt-4 flex items-center gap-2 text-sm text-green-600" data-testid="first-draw-sig-ready">
                  <CheckCircle2 className="w-4 h-4" /> Signature captured
                </div>
              )}
              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="first-draw-back-2"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                <Button onClick={generate} disabled={!signature || generating} className="bg-blue-600 hover:bg-blue-700" data-testid="first-draw-generate">
                  {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><FileText className="w-4 h-4 mr-2" /> Generate bank-ready PDF</>}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3 — Done */}
          {step === 3 && done && (
            <div className="text-center py-4" data-testid="first-draw-step-done">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Your draw is bank-ready 🎉</h2>
              <p className="text-slate-500 mb-6">We downloaded your DrawReady-branded draw request PDF. That's exactly what your bank receives.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={generate} variant="outline" data-testid="first-draw-redownload">
                  <Download className="w-4 h-4 mr-2" /> Download again
                </Button>
                <Button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700" data-testid="first-draw-signup-cta">
                  <Sparkles className="w-4 h-4 mr-2" /> Do this for my real project
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Sample data only — a fictitious builder and signature. DrawReady verifies package completeness; final approval remains with the lender.
        </p>
      </div>
    </div>
  );
};

export default FirstDraw;
